import AxiosInstance from "../Utils/axios/axiosConfig";

/**
 * Fetch all user guides from the API
 * @returns {Promise} Promise that resolves to the user guides data
 */
const getAllUserGuides = async () => {
    const response = await AxiosInstance.get("/user-guide/");
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to fetch user guides");
    }
};

/**
 * Get a single user guide by ID
 * @param {number|string} id - The user guide ID
 * @returns {Promise} Promise that resolves to the user guide data
 */
const getUserGuideById = async (id) => {
    const response = await AxiosInstance.get(`/user-guide/?id=${id}`);
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to fetch user guide");
    }
};

/**
 * Get a single user guide by name
 * @param {string} name - The user guide name/document name
 * @returns {Promise} Promise that resolves to the user guide data
 */
const getUserGuideByName = async (name) => {
    const response = await AxiosInstance.get("/user-guide/", {
        params: { name }
    });
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to fetch user guide");
    }
};

/**
 * Create a new user guide
 * @param {Object} userGuideData - The user guide data
 * @param {string} userGuideData.name - The user guide name
 * @param {string} userGuideData.path - The S3 file path where the .html file is stored
 * @param {string} userGuideData.publish_version - The publish version (e.g., "1.0.0")
 * @param {string} userGuideData.content - The content of the user guide
 * @param {number|null} userGuideData.parent_id - Optional parent ID for nested documents
 * @param {string} userGuideData.type - The type: "tab" or "page"
 * @returns {Promise} Promise that resolves to the created user guide data
 */
const createUserGuide = async (userGuideData) => {
    const payload = {
        name: userGuideData.name,
        path: userGuideData.path,
        content: userGuideData.content || "",
        type: userGuideData.type, // "tab" or "page"
    };

    // Include publish_version if provided (null for tabs/subtabs, "1.0.0" for pages)
    if (userGuideData.publish_version !== undefined) {
        payload.publish_version = userGuideData.publish_version;
    } else {
        // Default to "1.0.0" only if not explicitly set (backward compatibility)
        payload.publish_version = "1.0.0";
    }

    // Only include parent_id if it's provided (for nested documents)
    if (userGuideData.parent_id !== null && userGuideData.parent_id !== undefined) {
        payload.parent_id = userGuideData.parent_id;
    }

    // Include icon if provided (for tabs)
    if (userGuideData.icon !== undefined) {
        payload.icon = userGuideData.icon;
    }

    // Include publish_status if provided (for pages: 'draft' initially)
    if (userGuideData.publish_status !== undefined) {
        payload.publish_status = userGuideData.publish_status;
    }

    // Include actual_path if provided (for pages: full S3 path to HTML file)
    if (userGuideData.actual_path !== undefined) {
        payload.actual_path = userGuideData.actual_path;
    }

    const response = await AxiosInstance.post("/user-guide/", payload);
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to create user guide");
    }
};

/**
 * Update an existing user guide
 * @param {number|string} id - The user guide ID (mandatory query parameter)
 * @param {Object} userGuideData - The user guide data to update
 * @param {string} [userGuideData.name] - The user guide name
 * @param {string} [userGuideData.path] - The S3 file path
 * @param {string} [userGuideData.publish_version] - The publish version
 * @param {string} [userGuideData.content] - The content of the user guide
 * @returns {Promise} Promise that resolves to the updated user guide data
 */
const updateUserGuide = async (id, userGuideData) => {
    const payload = {};
    
    // Only include fields that are provided
    if (userGuideData.name !== undefined) payload.name = userGuideData.name;
    if (userGuideData.path !== undefined) payload.path = userGuideData.path;
    if (userGuideData.publish_version !== undefined) payload.publish_version = userGuideData.publish_version;
    if (userGuideData.content !== undefined) payload.content = userGuideData.content;
    if (userGuideData.icon !== undefined) payload.icon = userGuideData.icon;
    if (userGuideData.publish_status !== undefined) payload.publish_status = userGuideData.publish_status;
    if (userGuideData.actual_path !== undefined) payload.actual_path = userGuideData.actual_path;

    const response = await AxiosInstance.put("/user-guide/", payload, {
        params: { id }
    });
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to update user guide");
    }
};

/**
 * Delete a user guide
 * @param {number|string} id - The user guide ID (mandatory query parameter)
 * @returns {Promise} Promise that resolves to the deletion response
 */
const deleteUserGuide = async (id) => {
    const response = await AxiosInstance.delete("/user-guide/", {
        params: { id }
    });
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to delete user guide");
    }
};

/**
 * Search user guides
 * @param {string} pattern - The search keyword/pattern
 * @returns {Promise} Promise that resolves to the search results
 */
const searchUserGuides = async (pattern) => {
    const response = await AxiosInstance.get("/user-guide/", {
        params: { search: true, pattern }
    });
    if (!response.data.error) {
        return response.data;
    } else {
        return Promise.reject(response.data.msg || "Failed to search user guides");
    }
};

export {
    getAllUserGuides,
    getUserGuideById,
    getUserGuideByName,
    createUserGuide,
    updateUserGuide,
    deleteUserGuide,
    searchUserGuides
};

